import { SCREEN_RIGHT_WORLD_SIGN } from "../../config/GameConfig";
/**
 * KeyDispatcher — synthesizes KeyboardEvents on `window` so the
 * engine-side NavigationSystem can drive input without Playwright.
 *
 * InputManager listens to `window.addEventListener("keydown" | "keyup", ...)`
 * and normalizes keys via `.toLowerCase()` (see InputManager.normalizeKey),
 * so dispatching a `KeyboardEvent("keydown", { key: "ArrowLeft" })` flows
 * through the same path a physical press would.
 *
 * Two schemes are supported — one per cat — so the navigator can drive
 * both cats simultaneously in co-op mode without them fighting over the
 * same key.
 */

export type CatKind = "minna" | "basil";
export type InputMode = "solo" | "coop";

export interface CatKeyScheme {
  /**
   * Move toward larger world-X (positive moveX axis). "east" means the
   * dx > 0 direction; which key that is follows VIEW.MIRROR_X (with the
   * mirror on, world +X is screen-right, so it's the right key).
   */
  east: string;
  /** Move toward smaller world-X (negative moveX axis). */
  west: string;
  /** Jump. */
  jump: string;
  /** Basil's OverclockedLeap — ability keys route through keyboardAbility. */
  ovLeap: string | null;
}

/**
 * In solo mode, InputManager routes the ACTIVE cat through
 * `getActiveInput()` — a composite of keyboardP1 (WASD) + gamepad0 +
 * touch. Inactive cat receives `getIdleInput()`. So no matter which cat
 * is "active", walking is WASD. Ability digits go through the shared
 * keyboardAbility source.
 */
/** With VIEW.MIRROR_X, world +X ("east") is screen-right: the right key. */
const EAST_IS_RIGHT = SCREEN_RIGHT_WORLD_SIGN > 0;

const SCHEME_SOLO: CatKeyScheme = {
  east: EAST_IS_RIGHT ? "d" : "a", // positive moveX → world +X
  west: EAST_IS_RIGHT ? "a" : "d", // negative moveX → world -X
  jump: " ",
  ovLeap: "4",
};

/**
 * In co-op mode, both cats have their own input source.
 * Minna = keyboardP1 (WASD/Space), Basil = keyboardP2 (Arrows/Shift|Enter).
 * Keys follow SCREEN_RIGHT_WORLD_SIGN (per keyboardP2Movement).
 */
const SCHEME_COOP_MINNA: CatKeyScheme = {
  east: EAST_IS_RIGHT ? "d" : "a",
  west: EAST_IS_RIGHT ? "a" : "d",
  jump: " ",
  ovLeap: "4",
};

const SCHEME_COOP_BASIL: CatKeyScheme = {
  east: EAST_IS_RIGHT ? "ArrowRight" : "ArrowLeft", // positive moveX for P2
  west: EAST_IS_RIGHT ? "ArrowLeft" : "ArrowRight", // negative moveX for P2
  jump: "Enter",
  ovLeap: "4",
};

export function schemeFor(cat: CatKind, mode: InputMode): CatKeyScheme {
  if (mode === "solo") return SCHEME_SOLO;
  return cat === "minna" ? SCHEME_COOP_MINNA : SCHEME_COOP_BASIL;
}

function dispatch(type: "keydown" | "keyup", key: string): void {
  const ev = new KeyboardEvent(type, {
    key,
    code: key,
    bubbles: true,
    cancelable: true,
  });
  window.dispatchEvent(ev);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Per-cat key holder. `hold()` transitions the held direction; `release()`
 * clears it; `tap()` presses-and-releases with a cooldown to prevent
 * rapid re-taps (mirrors the 200ms `jumpArmedAt` guard in the Node-side
 * navigator).
 */
export class CatKeyDispatcher {
  private heldDir: string | null = null;
  private jumpArmedAt = 0;
  private readonly scheme: CatKeyScheme;

  constructor(cat: CatKind, mode: InputMode = "solo") {
    this.scheme = schemeFor(cat, mode);
  }

  get scheme_(): CatKeyScheme {
    return this.scheme;
  }

  /**
   * Transition held direction. Releases the previous direction (if any)
   * and presses the new one. `null` releases without pressing. Dispatch is
   * synchronous — KeyboardEvents are queued through the event loop but
   * hit window handlers before the next microtask.
   */
  hold(dir: "east" | "west" | null): void {
    const key = dir === "east" ? this.scheme.east : dir === "west" ? this.scheme.west : null;
    if (this.heldDir === key) return;
    if (this.heldDir) {
      dispatch("keyup", this.heldDir);
    }
    if (key) {
      dispatch("keydown", key);
    }
    this.heldDir = key;
  }

  releaseAll(): void {
    if (this.heldDir) {
      dispatch("keyup", this.heldDir);
      this.heldDir = null;
    }
  }

  // Hold durations for tap-jump and tap-ovleap must exceed the apex time
  // so that a future release-only variable-jump-cut (which fires on
  // justReleased('jump') while vy > 0) is always a no-op for navigator
  // driven jumps.
  //
  // Apex time = vyInit / |GRAVITY_Y|
  //   Normal jump : JUMP_IMPULSE (9) / |GRAVITY_Y| (15) ≈ 0.60 s → 700 ms hold
  //   OverclockedLeap: JUMP_IMPULSE * leapMultiplier (9 × 2.5 = 22.5) / 15 ≈ 1.50 s → 1600 ms hold
  //
  // If these physics constants change, update the hold values accordingly.
  static readonly TAP_JUMP_HOLD_MS = 700;   // > 9/15 s ≈ 600 ms apex
  static readonly TAP_OV_LEAP_HOLD_MS = 1600; // > 22.5/15 s ≈ 1500 ms apex

  async tapJump(holdMs = CatKeyDispatcher.TAP_JUMP_HOLD_MS): Promise<void> {
    if (Date.now() - this.jumpArmedAt < 200) return;
    this.jumpArmedAt = Date.now();
    dispatch("keydown", this.scheme.jump);
    await delay(holdMs);
    dispatch("keyup", this.scheme.jump);
  }

  async tapOvLeap(holdMs = CatKeyDispatcher.TAP_OV_LEAP_HOLD_MS): Promise<void> {
    if (!this.scheme.ovLeap) return;
    if (Date.now() - this.jumpArmedAt < 200) return;
    this.jumpArmedAt = Date.now();
    dispatch("keydown", this.scheme.ovLeap);
    await delay(holdMs);
    dispatch("keyup", this.scheme.ovLeap);
  }

  /** Whether a direction is currently held. */
  heldDirection(): "east" | "west" | null {
    if (!this.heldDir) return null;
    if (this.heldDir === this.scheme.east) return "east";
    if (this.heldDir === this.scheme.west) return "west";
    return null;
  }
}

/**
 * Raw key tap used by traversal recipes and one-off activation. Not tied
 * to a cat scheme — callers pass the literal key. Kept alongside the
 * dispatcher because it shares the synthesized-KeyboardEvent primitive.
 */
export async function tapKey(key: string, holdMs = 80): Promise<void> {
  dispatch("keydown", key);
  await delay(holdMs);
  dispatch("keyup", key);
}

export function pressKeyDown(key: string): void {
  dispatch("keydown", key);
}

export function pressKeyUp(key: string): void {
  dispatch("keyup", key);
}
